/**
 * COMPOSANT DE PAGINATION - Version corrigée avec pointillés
 */
const PaginationComponent = ({ currentPage, totalCount, pageSize, handlePageChange }) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  if (totalPages <= 1) return null;

  const generatePageNumbers = () => {
    const pages = [];
    const delta = 1; // Nombre de pages à afficher de chaque côté
    
    // Toujours afficher la première page
    pages.push(1);
    
    // Pointillés après la première page si nécessaire
    if (currentPage - delta > 2) {
      pages.push('...');
    }
    
    // Pages autour de la page courante
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      // Éviter les doublons
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
    
    // Pointillés avant la dernière page si nécessaire
    if (currentPage + delta < totalPages - 1) {
      // Éviter les pointillés doubles
      if (!pages.includes('...') || pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    
    // Toujours afficher la dernière page si plus d'une page
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <nav aria-label="Pagination navigation" className="pagination-custom">
      {/* INFORMATION DE PAGE - Positionnée correctement au-dessus */}
      <div className="text-center mb-3">
        <small className="text-muted fw-medium mb-4">
          Page {currentPage} sur {totalPages} • {totalCount.toLocaleString()} compte(s) total
        </small>
      </div>
      
      <ul className="pagination pagination-sm justify-content-center m-0">
        {/* Bouton Précédent */}
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button 
            className="page-link border-0 rounded-3 shadow-sm" 
            onClick={() => handlePageChange(currentPage - 1)} 
            disabled={currentPage === 1}
            aria-label="Page précédente"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
        </li>

        {/* Pages numérotées avec pointillés */}
        {pageNumbers.map((pageNum, index) => (
          <li 
            key={index} 
            className={`page-item ${pageNum === currentPage ? 'active' : ''} ${pageNum === '...' ? 'disabled' : ''}`}
          >
            {pageNum === '...' ? (
              <span className="page-link border-0 rounded-3 shadow-sm fw-semibold text-muted">
                ...
              </span>
            ) : (
              <button 
                className="page-link border-0 rounded-3 mx-1 shadow-sm fw-semibold" 
                onClick={() => handlePageChange(pageNum)}
                aria-label={`Page ${pageNum}`}
                aria-current={pageNum === currentPage ? 'page' : undefined}
              >
                {pageNum}
              </button>
            )}
          </li>
        ))}

        {/* Bouton Suivant */}
        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
          <button 
            className="page-link border-0 rounded-3 shadow-sm" 
            onClick={() => handlePageChange(currentPage + 1)} 
            disabled={currentPage === totalPages}
            aria-label="Page suivante"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </li>
      </ul>
    </nav>
  );
};

/**
 * SÉLECTEUR DE TAILLE DE PAGE - Version corrigée
 */
const PageSizeSelector = ({ pageSize, setPageSize, setCurrentPage }) => {
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // Retour à la première page
  };

  return (
    <div className="page-size-selector-professional">
      <div className="d-flex align-items-center gap-3 bg-light px-3 py-2 rounded-3 border">
        <span className="fw-semibold text-dark d-flex align-items-center gap-2">
          <i className="fas fa-list-ul text-primary"></i>
          Affichage
        </span>
        
        <div className="position-relative">
          <select 
            id="pageSize"
            className="account-type-select border-primary shadow-sm"
            value={pageSize} 
            onChange={handlePageSizeChange}
            style={{ minWidth: '120px', appearance: 'none', paddingRight: '2.5rem' }}
            aria-label="Nombre de lignes par page"
          >
            <option value={10}>10 lignes</option>
            <option value={25}>25 lignes</option>
            <option value={50}>50 lignes</option>
            <option value={100}>100 lignes</option>
            <option value={150}>150 lignes</option>
            <option value={200}>200 lignes</option>
            <option value={250}>250 lignes</option>
            <option value={300}>300 lignes</option>
          </select>
          <div className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted">
            <i className="fas fa-chevron-down"></i>
          </div>
        </div>
        
        <span className="text-muted fw-medium">par page</span>
      </div>
    </div>
  );
};

/**
 * SECTION DE PAGINATION COMPLÈTE - Version finale
 */
const PaginationSection = ({ 
  currentPage, 
  totalCount, 
  pageSize, 
  setPageSize, 
  setCurrentPage, 
  handlePageChange 
}) => {
  return (
    <div className="pagination-section bg-white border-top py-3 mt-3 rounded-3">
      <div className="container-fluid">
        <div className="row align-items-center justify-content-between">
          <div className="col-auto">
            <PageSizeSelector 
              pageSize={pageSize}
              setPageSize={setPageSize}
              setCurrentPage={setCurrentPage}
            />
          </div>
          <div className="col-auto">
            <PaginationComponent 
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={pageSize}
              handlePageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaginationSection;